import { motion } from 'framer-motion';
import { FileText, ExternalLink, Quote } from 'lucide-react';
import { useUserProfile } from '../../hooks/useUserProfile';

interface EntryPreviewCardProps {
    topic: string;
    authorPubkey?: string;
    content?: string;
    onClick?: () => void;
}

/**
 * EntryPreviewCard - Displays a rich preview of a dictionary entry shared in chat
 * Enhanced with premium aesthetics and proper encoding
 */
export function EntryPreviewCard({ topic, authorPubkey, content, onClick }: EntryPreviewCardProps) {
    const { profile } = useUserProfile(authorPubkey || null);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            whileHover={{ scale: 1.01 }}
            onClick={onClick}
            className="mt-3 overflow-hidden relative group cursor-pointer"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-mavi-500/5 to-mavi-600/10 dark:from-mavi-400/10 dark:to-mavi-500/5 rounded-2xl" />

            <div className="relative p-4 bg-white/40 dark:bg-black/20 backdrop-blur-xl border border-white/40 dark:border-gray-700/30 rounded-2xl shadow-sm group-hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                    <div className="relative flex-shrink-0">
                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-mavi-500 to-mavi-600 text-white shadow-lg shadow-mavi-500/20">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white dark:bg-surface-800 flex items-center justify-center p-0.5 shadow-sm border border-surface-100 dark:border-surface-700">
                            <ExternalLink className="w-2.5 h-2.5 text-mavi-500" />
                        </div>
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <h4 className="font-poppins font-bold text-base text-gray-900 dark:text-white truncate group-hover:text-mavi-500 transition-colors leading-tight">
                                {topic}
                            </h4>
                        </div>

                        {profile && (
                            <div className="flex items-center gap-1.5 mt-1">
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">yazar:</span>
                                <span className="text-[10px] text-mavi-500 font-bold hover:underline">{profile.name}</span>
                            </div>
                        )}

                        {content && (
                            <div className="relative mt-3">
                                <Quote className="absolute -left-1 -top-1 w-3 h-3 text-mavi-500/20 rotate-180" />
                                <p className="text-xs text-gray-600 dark:text-gray-300 pl-3 line-clamp-2 font-merriweather italic opacity-80 leading-relaxed border-l-2 border-mavi-500/20">
                                    {content.replace(/<[^>]*>?/gm, '')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-4 pt-3 border-t border-black/5 dark:border-white/5 flex items-center justify-between">
                    <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-mavi-500/40" />
                        <div className="w-1.5 h-1.5 rounded-full bg-mavi-500/20" />
                    </div>
                    <span className="text-[10px] font-bold text-mavi-600 dark:text-mavi-400 group-hover:translate-x-1 transition-transform uppercase tracking-widest flex items-center gap-1.5">
                        Sözlükte Devam Et <ExternalLink className="w-2.5 h-2.5" />
                    </span>
                </div>
            </div>
        </motion.div>
    );
}
